<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" class="d-flex align-center flex-wrap" style="gap: 12px">
        <h2>Liquidaciones a comercios</h2>
        <v-spacer />
        <v-btn text :loading="loading" @click="load()"><v-icon left>mdi-refresh</v-icon> Actualizar</v-btn>
        <v-btn outlined color="primary" :loading="running" @click="run(true)"><v-icon left>mdi-calculator-variant-outline</v-icon> Simular remisiones de hoy</v-btn>
        <v-btn color="primary" :loading="running" @click="confirmRun = true"><v-icon left>mdi-send</v-icon> Emitir remisiones de hoy</v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" dense dismissible @input="error = ''">{{ error }}</v-alert>
    <v-alert v-if="runResult" type="info" dense text dismissible @input="runResult = null">
      <div class="font-weight-bold">{{ runResult.dry ? 'Simulación (no se guardó ni se envió nada)' : 'Remisiones emitidas' }}</div>
      <div v-if="!runResult.items.length">No hay pedidos pendientes por remitir hoy.</div>
      <div v-for="(r, i) in runResult.items" :key="i">
        {{ r.store }}: <span v-if="r.error" class="red--text">{{ r.error }}</span><span v-else>{{ r.orders }} pedidos · neto {{ money(r.net) }}<span v-if="r.number"> · {{ r.number }}</span></span>
      </div>
    </v-alert>

    <v-tabs v-model="tab" background-color="transparent">
      <v-tab>Resumen por comercio</v-tab>
      <v-tab>Remisiones <v-chip v-if="unpaidCount" x-small class="ml-2" color="orange" dark>{{ unpaidCount }} por pagar</v-chip></v-tab>
      <v-tab>Domiciliarios de Festy <v-chip v-if="riderUnpaid" x-small class="ml-2" color="orange" dark>{{ riderUnpaid }} por pagar</v-chip></v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="mt-3" style="background: transparent">
      <!-- RESUMEN -->
      <v-tab-item>
        <v-row>
          <v-col cols="6" md="3"><v-card class="pa-4"><div class="caption grey--text">POR LIQUIDAR (sin remisión)</div><div class="headline font-weight-bold">{{ money(totals.accrued) }}</div></v-card></v-col>
          <v-col cols="6" md="3"><v-card class="pa-4"><div class="caption grey--text">REMISIONES POR PAGAR</div><div class="headline font-weight-bold orange--text text--darken-2">{{ money(totals.toPay) }}</div></v-card></v-col>
          <v-col cols="6" md="3"><v-card class="pa-4"><div class="caption grey--text">PAGADO</div><div class="headline font-weight-bold green--text text--darken-2">{{ money(totals.paid) }}</div></v-card></v-col>
          <v-col cols="6" md="3"><v-card class="pa-4"><div class="caption grey--text">DEUDA TOTAL CON COMERCIOS</div><div class="headline font-weight-bold">{{ money(totals.accrued + totals.toPay) }}</div></v-card></v-col>
        </v-row>
        <v-data-table :headers="summaryHeaders" :items="summary" :loading="loading" :items-per-page="15" class="elevation-1 mt-3" no-data-text="Aún no hay pedidos entregados">
          <template #item.rate="{ item }">{{ Math.round(item.rate * 1000) / 10 }}%</template>
          <template #item.frequency="{ item }">{{ freqLabel(item.frequency) }}</template>
          <template #item.accrued="{ item }">{{ money(item.accrued) }}</template>
          <template #item.toPay="{ item }"><b :class="item.toPay ? 'orange--text text--darken-2' : ''">{{ money(item.toPay) }}</b></template>
          <template #item.paid="{ item }">{{ money(item.paid) }}</template>
          <template #item.accountType="{ item }">
            <a v-if="item.certUrl" :href="item.certUrl" target="_blank" rel="noopener">{{ item.accountType }}</a>
            <v-chip v-else x-small color="orange" dark>Sin cuenta</v-chip>
          </template>
          <template #item.actions="{ item }">
            <v-btn icon small title="Tarifa y frecuencia" @click="editStore(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn icon small title="Ver sus remisiones" @click="filterStore = item.storeId; tab = 1; loadSettlements()"><v-icon small>mdi-file-document-outline</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- REMISIONES -->
      <v-tab-item>
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px">
          <v-select v-model="filterStatus" :items="statusItems" dense outlined hide-details style="max-width: 200px" @change="loadSettlements()" />
          <v-chip v-if="filterStore" close small @click:close="filterStore = ''; loadSettlements()">Filtrado por comercio</v-chip>
        </div>
        <v-data-table :headers="settlementHeaders" :items="settlements" :loading="loadingList" :items-per-page="15" class="elevation-1" no-data-text="No hay remisiones" style="cursor: pointer" @click:row="openSettlement">
          <template #item.issuedAt="{ item }">{{ fecha(item.issuedAt) }}</template>
          <template #item.period="{ item }">{{ dia(item.periodStart) }} al {{ dia(item.periodEnd) }}</template>
          <template #item.gross="{ item }">{{ money(item.gross) }}</template>
          <template #item.commission="{ item }">{{ money(item.commission) }}</template>
          <template #item.net="{ item }"><b>{{ money(item.net) }}</b></template>
          <template #item.status="{ item }"><v-chip small :color="item.status === 'paid' ? 'green' : 'orange'" dark>{{ item.status === 'paid' ? 'Pagada' : 'Por pagar desde ' + dia(item.dueDate) }}</v-chip></template>
          <template #item.actions="{ item }">
            <v-btn v-if="item.status !== 'paid'" small color="success" depressed @click.stop="startPay(item)">Marcar pagada</v-btn>
          </template>
        </v-data-table>
      </v-tab-item>
      <!-- DOMICILIARIOS DE FESTY -->
      <v-tab-item>
        <v-alert type="info" dense text class="mt-1">
          La propina que deja el cliente va 100% para el domiciliario. Si el pedido lo entregó un domiciliario de <b>Festy</b>, se le liquida a él cada lunes. Si lo entregó uno del <b>comercio</b> (o nadie asignado), el domicilio y la propina quedan como saldo del comercio en su remisión.
          Marca a cada domiciliario como "Festy" o "Del comercio" con el lápiz. Si no lo marcas, se decide por la relación con la tienda.
        </v-alert>
        <v-data-table :headers="riderHeaders" :items="riders" :loading="loadingRiders" :items-per-page="15" class="elevation-1" no-data-text="Aún no hay propinas de domiciliarios de Festy">
          <template #item.fleet="{ item }"><v-chip x-small :color="item.fleet === 'festy' ? 'primary' : item.fleet === 'store' ? 'grey' : ''" :dark="!!item.fleet">{{ item.fleet === 'festy' ? 'Festy' : item.fleet === 'store' ? 'Del comercio' : 'Sin definir' }}</v-chip></template>
          <template #item.accrued="{ item }">{{ money(item.accrued) }}</template>
          <template #item.toPay="{ item }"><b :class="item.toPay ? 'orange--text text--darken-2' : ''">{{ money(item.toPay) }}</b></template>
          <template #item.paid="{ item }">{{ money(item.paid) }}</template>
          <template #item.actions="{ item }"><v-btn icon small title="Tipo de domiciliario" @click="startFleet(item)"><v-icon small>mdi-pencil</v-icon></v-btn></template>
        </v-data-table>
        <h3 class="subtitle-1 font-weight-bold mt-5 mb-2">Liquidaciones de propinas</h3>
        <v-data-table :headers="riderSettlementHeaders" :items="riderSettlements" :items-per-page="10" class="elevation-1" no-data-text="Aún no hay liquidaciones de domiciliarios" style="cursor: pointer" @click:row="openSettlement">
          <template #item.issuedAt="{ item }">{{ fecha(item.issuedAt) }}</template>
          <template #item.net="{ item }"><b>{{ money(item.net) }}</b></template>
          <template #item.status="{ item }"><v-chip small :color="item.status === 'paid' ? 'green' : 'orange'" dark>{{ item.status === 'paid' ? 'Pagada' : 'Por pagar' }}</v-chip></template>
          <template #item.actions="{ item }"><v-btn v-if="item.status !== 'paid'" small color="success" depressed @click.stop="startPay(item)">Marcar pagada</v-btn></template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>

    <!-- Tipo de domiciliario -->
    <v-dialog v-model="fleetDialog" max-width="380">
      <v-card v-if="fleetItem">
        <v-card-title class="subtitle-1">{{ fleetItem.name }}</v-card-title>
        <v-card-text>
          <v-radio-group v-model="fleetValue">
            <v-radio label="Domiciliario de Festy (le liquidamos sus propinas)" value="festy" />
            <v-radio label="Domiciliario del comercio (el saldo es del comercio)" value="store" />
          </v-radio-group>
          <p class="caption grey--text">Aplica a los pedidos que se entreguen desde ahora.</p>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="fleetDialog = false">Cancelar</v-btn><v-btn color="primary" :loading="savingFleet" @click="saveFleet">Guardar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detalle -->
    <v-dialog v-model="detailDialog" max-width="760" scrollable>
      <v-card v-if="detail">
        <v-card-title class="subtitle-1">Remisión {{ detail.number }} · {{ detail.kind === 'rider' ? detail.riderName : detail.storeName }}</v-card-title>
        <v-card-subtitle>{{ detail.orderCount }} pedidos · {{ dia(detail.periodStart) }} al {{ dia(detail.periodEnd) }}</v-card-subtitle>
        <v-card-text>
          <v-simple-table dense>
            <thead><tr><th>Pedido</th><th>Fecha</th><th>Pago</th><th class="text-right">Productos</th><th class="text-right">Fee del pedido</th><th class="text-right">Domicilio y propina</th><th class="text-right">Neto</th></tr></thead>
            <tbody>
              <tr v-for="l in detail.lines" :key="l.orderId"><td>{{ l.orderId }}</td><td>{{ fecha(l.date) }}</td><td>{{ methodLabel(l.method) }}</td><td class="text-right">{{ money(l.goods) }}</td><td class="text-right">{{ money(l.commission) }}</td><td class="text-right">{{ l.courier ? '+' + money(l.courier) : '' }}</td><td class="text-right"><b>{{ money(l.net) }}</b></td></tr>
            </tbody>
          </v-simple-table>
          <div class="mt-3 text-right">
            <div>Productos: <b>{{ money(detail.gross) }}</b> · Fee del pedido ({{ Math.round(detail.rate * 1000) / 10 }}%): <b>{{ money(detail.commission) }}</b></div>
            <div v-if="detail.courierCredit">Domicilio y propina (domiciliarios del comercio): <b>+{{ money(detail.courierCredit) }}</b></div>
            <div class="title">A pagar: {{ money(detail.net) }}</div>
            <div v-if="detail.status === 'paid'" class="green--text text--darken-2">Pagada {{ fecha(detail.paidAt) }} · {{ money(detail.paidAmount) }}<span v-if="detail.paymentRef"> · Ref. {{ detail.paymentRef }}</span></div>
          </div>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="detailDialog = false">Cerrar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Marcar pagada -->
    <v-dialog v-model="payDialog" max-width="440">
      <v-card v-if="payItem">
        <v-card-title class="subtitle-1">Registrar pago de {{ payItem.number }}</v-card-title>
        <v-card-text>
          <p class="body-2">{{ payItem.kind === 'rider' ? payItem.riderName : payItem.storeName }} · neto {{ money(payItem.net) }}. Al guardar queda registrada como pagada (a los comercios se les envía un aviso por correo).</p>
          <p v-if="payItem.certUrl" class="body-2">Cuenta <b>{{ payItem.accountType }}</b> · <a :href="payItem.certUrl" target="_blank" rel="noopener">ver certificación bancaria</a></p>
          <v-alert v-else-if="payItem.kind !== 'rider'" type="warning" dense text class="body-2">Este comercio aún no ha cargado su cuenta de pagos.</v-alert>
          <v-text-field v-model="payAmount" label="Valor transferido" prefix="$" outlined dense />
          <v-text-field v-model="payRef" label="Referencia de la transferencia" outlined dense />
          <v-textarea v-model="payNotes" label="Notas (opcional)" outlined dense rows="2" />
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="payDialog = false">Cancelar</v-btn><v-btn color="success" :loading="paying" @click="confirmPay">Guardar pago</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tarifa y frecuencia -->
    <v-dialog v-model="storeDialog" max-width="420">
      <v-card v-if="storeEdit">
        <v-card-title class="subtitle-1">{{ storeEdit.name }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="storeRate" label="Fee del pedido (%)" suffix="%" outlined dense hint="Se aplica a los pedidos que se entreguen desde ahora. Los ya entregados conservan su tarifa." persistent-hint />
          <v-select v-model="storeFreq" :items="freqItems" label="Frecuencia de la remisión" outlined dense class="mt-4" />
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="storeDialog = false">Cancelar</v-btn><v-btn color="primary" :loading="savingStore" @click="saveStore">Guardar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmar emision -->
    <v-dialog v-model="confirmRun" max-width="440">
      <v-card>
        <v-card-title class="subtitle-1">Emitir remisiones de hoy</v-card-title>
        <v-card-text>Se generan las remisiones que tocan hoy según la frecuencia de cada comercio y se envían por correo. Normalmente esto ocurre solo cada mañana. ¿Continuar?</v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="confirmRun = false">Cancelar</v-btn><v-btn color="primary" @click="confirmRun = false; run(false)">Emitir</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Parse from 'parse'

const FREQ = { daily: 'Diaria', weekly: 'Semanal (lunes)', monthly: 'Mensual (día 1)' }

export default {
  name: 'Settlements',
  data: () => ({
    tab: 0,
    loading: false,
    loadingList: false,
    running: false,
    error: '',
    runResult: null,
    summary: [],
    settlements: [],
    filterStatus: '',
    filterStore: '',
    unpaidCount: 0,
    riders: [],
    riderSettlements: [],
    loadingRiders: false,
    riderUnpaid: 0,
    fleetDialog: false,
    fleetItem: null,
    fleetValue: 'festy',
    savingFleet: false,
    riderHeaders: [
      { text: 'Domiciliario', value: 'name' },
      { text: 'Tipo', value: 'fleet' },
      { text: 'Pedidos con propina', value: 'orders', align: 'end' },
      { text: 'Por liquidar', value: 'accrued', align: 'end' },
      { text: 'Por pagar', value: 'toPay', align: 'end' },
      { text: 'Pagado', value: 'paid', align: 'end' },
      { text: '', value: 'actions', sortable: false, align: 'end' }
    ],
    riderSettlementHeaders: [
      { text: 'Liquidación', value: 'number' },
      { text: 'Domiciliario', value: 'riderName' },
      { text: 'Emitida', value: 'issuedAt' },
      { text: 'Propinas', value: 'orderCount', align: 'end' },
      { text: 'A pagar', value: 'net', align: 'end' },
      { text: 'Estado', value: 'status' },
      { text: '', value: 'actions', sortable: false, align: 'end' }
    ],
    detailDialog: false,
    detail: null,
    payDialog: false,
    payItem: null,
    payAmount: '',
    payRef: '',
    payNotes: '',
    paying: false,
    storeDialog: false,
    storeEdit: null,
    storeRate: '',
    storeFreq: 'weekly',
    savingStore: false,
    confirmRun: false,
    statusItems: [{ text: 'Todas', value: '' }, { text: 'Por pagar', value: 'issued' }, { text: 'Pagadas', value: 'paid' }],
    freqItems: Object.keys(FREQ).map(v => ({ value: v, text: FREQ[v] })),
    summaryHeaders: [
      { text: 'Comercio', value: 'name' },
      { text: 'Fee del pedido', value: 'rate', align: 'end' },
      { text: 'Remisión', value: 'frequency' },
      { text: 'Pedidos sin remitir', value: 'orders', align: 'end' },
      { text: 'Por liquidar', value: 'accrued', align: 'end' },
      { text: 'Por pagar', value: 'toPay', align: 'end' },
      { text: 'Pagado', value: 'paid', align: 'end' },
      { text: 'Cuenta', value: 'accountType' },
      { text: '', value: 'actions', sortable: false, align: 'end' }
    ],
    settlementHeaders: [
      { text: 'Remisión', value: 'number' },
      { text: 'Comercio', value: 'storeName' },
      { text: 'Emitida', value: 'issuedAt' },
      { text: 'Periodo', value: 'period', sortable: false },
      { text: 'Pedidos', value: 'orderCount', align: 'end' },
      { text: 'Productos', value: 'gross', align: 'end' },
      { text: 'Fee del pedido', value: 'commission', align: 'end' },
      { text: 'A pagar', value: 'net', align: 'end' },
      { text: 'Estado', value: 'status' },
      { text: '', value: 'actions', sortable: false, align: 'end' }
    ]
  }),
  computed: {
    totals () {
      return this.summary.reduce((t, s) => ({ accrued: t.accrued + s.accrued, toPay: t.toPay + s.toPay, paid: t.paid + s.paid }), { accrued: 0, toPay: 0, paid: 0 })
    }
  },
  mounted () { this.load() },
  methods: {
    money: n => '$' + String(Math.round(Number(n) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
    dia: d => (d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''),
    fecha: d => (d ? new Date(d).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : ''),
    freqLabel: f => FREQ[f] || f,
    methodLabel: m => (m === 'wompi' ? 'En línea (Wompi)' : m === 'tip' ? 'Propina' : m === 'OnDelivery' ? 'Efectivo' : m === 'digitalTransfer' ? 'Transferencia' : m === 'cardReader' ? 'Datáfono' : m || ''),
    msg: e => (e && e.message) || String(e),
    async load () {
      this.loading = true
      try {
        this.summary = await Parse.Cloud.run('getSettlementsSummaryBO')
        await this.loadSettlements()
        await this.loadRiders()
      } catch (e) { this.error = this.msg(e) } finally { this.loading = false }
    },
    async loadRiders () {
      this.loadingRiders = true
      try {
        const [riders, settlements] = await Promise.all([
          Parse.Cloud.run('getRiderBalancesBO'),
          Parse.Cloud.run('getSettlementsBO', { kind: 'rider' })
        ])
        this.riders = riders
        this.riderSettlements = settlements
        this.riderUnpaid = settlements.filter(s => s.status !== 'paid').length
      } catch (e) { this.error = this.msg(e) } finally { this.loadingRiders = false }
    },
    startFleet (item) {
      this.fleetItem = item
      this.fleetValue = item.fleet || 'festy'
      this.fleetDialog = true
    },
    async saveFleet () {
      this.savingFleet = true
      try {
        await Parse.Cloud.run('setRiderFleetBO', { riderId: this.fleetItem.riderId, fleet: this.fleetValue })
        this.fleetDialog = false
        await this.loadRiders()
      } catch (e) { this.error = this.msg(e) } finally { this.savingFleet = false }
    },
    async loadSettlements () {
      this.loadingList = true
      try {
        const params = {}
        if (this.filterStatus) params.status = this.filterStatus
        if (this.filterStore) params.storeId = this.filterStore
        this.settlements = await Parse.Cloud.run('getSettlementsBO', params)
        if (!this.filterStatus && !this.filterStore) this.unpaidCount = this.settlements.filter(s => s.status !== 'paid').length
      } catch (e) { this.error = this.msg(e) } finally { this.loadingList = false }
    },
    async run (dry) {
      this.running = true
      try {
        const items = await Parse.Cloud.run('runStatementsBO', { dryRun: !!dry })
        this.runResult = { dry: !!dry, items }
        if (!dry) await this.load()
      } catch (e) { this.error = this.msg(e) } finally { this.running = false }
    },
    async openSettlement (item) {
      try {
        this.detail = await Parse.Cloud.run('getSettlementBO', { id: item.id })
        this.detailDialog = true
      } catch (e) { this.error = this.msg(e) }
    },
    startPay (item) {
      this.payItem = item
      this.payAmount = String(item.net)
      this.payRef = ''
      this.payNotes = ''
      this.payDialog = true
    },
    async confirmPay () {
      this.paying = true
      try {
        const amount = Number(String(this.payAmount).replace(/[^\d]/g, ''))
        await Parse.Cloud.run('markSettlementPaidBO', { id: this.payItem.id, paidAmount: amount, reference: this.payRef, notes: this.payNotes })
        this.payDialog = false
        await this.load()
      } catch (e) { this.error = this.msg(e) } finally { this.paying = false }
    },
    editStore (item) {
      this.storeEdit = item
      this.storeRate = String(Math.round(item.rate * 1000) / 10)
      this.storeFreq = item.frequency
      this.storeDialog = true
    },
    async saveStore () {
      this.savingStore = true
      try {
        const rate = Number(String(this.storeRate).replace(',', '.')) / 100
        await Parse.Cloud.run('setStoreCommissionBO', { storeId: this.storeEdit.storeId, rate, frequency: this.storeFreq })
        this.storeDialog = false
        await this.load()
      } catch (e) { this.error = this.msg(e) } finally { this.savingStore = false }
    }
  }
}
</script>
